import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useDeleteCompany } from '@/hooks/useDeleteCompany'
import { Loader2, AlertTriangle, ShieldCheck, ShieldAlert, Building2, Users } from 'lucide-react'

interface DeleteCompanyDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  companyId: string
  companyName: string
  userCount: number
  onSuccess?: () => void
}

/**
 * DeleteCompanyDialog Component
 *
 * Dialog for deleting a company with the following features:
 * - Confirmation dialog with cascade deletion warning (all users will be deleted)
 * - Company information display (name and user count)
 * - Confirmation text input requirement (must type company name to confirm)
 * - Loading state during deletion
 * - Success/error notifications
 * - Refresh callback for updating company list after successful deletion
 */
export function DeleteCompanyDialog({
  open,
  onOpenChange,
  companyId,
  companyName,
  userCount,
  onSuccess,
}: DeleteCompanyDialogProps) {
  const [showSuccess, setShowSuccess] = useState(false)
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [confirmationText, setConfirmationText] = useState('')

  const { deleteCompany, loading, error } = useDeleteCompany()

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setShowSuccess(false)
      setShowError(false)
      setErrorMessage('')
      setConfirmationText('')
    }
  }, [open])

  const handleDelete = async () => {
    // Require confirmation text to match company name
    if (confirmationText !== companyName) {
      setShowError(true)
      setErrorMessage('Please type the company name to confirm deletion')
      return
    }

    const result = await deleteCompany(companyId)

    if (result) {
      setShowSuccess(true)
      setShowError(false)

      // Close dialog after showing success
      setTimeout(() => {
        onOpenChange(false)
        setShowSuccess(false)
        onSuccess?.()
      }, 1500)
    } else {
      setShowError(true)
      setErrorMessage(error || 'Failed to delete company')
    }
  }

  const canDelete = confirmationText === companyName && !loading

  return (
    <>
      {/* Success Toast */}
      {showSuccess && (
        <div className="fixed bottom-4 right-4 z-50 flex items-center gap-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200 px-4 py-3 rounded-lg shadow-lg animate-in slide-in-from-right-5 duration-300">
          <ShieldCheck className="h-5 w-5" />
          <div>
            <p className="font-medium">Company deleted successfully</p>
            <p className="text-sm opacity-90">{companyName} and all associated data have been removed</p>
          </div>
        </div>
      )}

      {/* Error Toast */}
      {showError && (
        <div className="fixed bottom-4 right-4 z-50 flex items-center gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg shadow-lg animate-in slide-in-from-right-5 duration-300">
          <ShieldAlert className="h-5 w-5" />
          <div>
            <p className="font-medium">Failed to delete company</p>
            <p className="text-sm opacity-90">{errorMessage}</p>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Delete Company
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <span className="font-semibold text-gray-900 dark:text-gray-100">{companyName}</span>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Company Information */}
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-3">
              <div className="flex items-center gap-3">
                <Building2 className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">
                    Company Name
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-1">{companyName}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                <div className="flex-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wide">
                    Total Users
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">{userCount} user{userCount !== 1 ? 's' : ''}</p>
                </div>
              </div>
            </div>

            {/* Cascade Deletion Warning */}
            <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  Cascade Deletion Warning
                </p>
                <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                  Deleting this company will <strong>permanently delete all {userCount} user{userCount !== 1 ? 's' : ''}</strong> associated with this company, along with all their data and content.
                </p>
              </div>
            </div>

            {/* Additional Warning */}
            <div className="text-xs text-gray-600 dark:text-gray-400 space-y-2">
              <p className="font-medium">This action will:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Permanently delete the company profile</li>
                <li>Delete all {userCount} user{userCount !== 1 ? 's' : ''} associated with this company</li>
                <li>Revoke all access and permissions for these users</li>
                <li>Delete all company content and activity data</li>
                <li><strong>CANNOT be reversed once completed</strong></li>
              </ul>
            </div>

            {/* Confirmation Input */}
            <div className="space-y-2">
              <label htmlFor="confirmation" className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Type the company name to confirm
              </label>
              <input
                id="confirmation"
                type="text"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder={companyName}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                This action requires you to type <span className="font-semibold">{companyName}</span> to confirm
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={!canDelete}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <AlertTriangle className="mr-2 h-4 w-4" />
                  Delete Company
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
